import { Typography } from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import React, { useMemo } from "react";

import { activityApi } from "apis";
import {
  MetricValueAggregatedResponseDto,
  MetricValuesAggregationRequestDto,
  MetricValuesAggregationRequestDtoIntervalEnum,
  MetricValuesAggregationRequestDtoAggregateTypesEnum,
  MetricValuesAggregationRequestDtoMetricCodeEnum,
  MetricValuesAggregationRequestDtoHealthProviderCodeEnum,
} from "libs/cellera-sdk";
import MetricCard, { MetricData } from "./MetricCard";
import SectionWrapper from "./SectionWrapper";

// Display info for the two overview cards
const HEIGHT_DISPLAY = {
  id: MetricValuesAggregationRequestDtoMetricCodeEnum.Height,
  name: "Height",
  unit: "ft",
} as const;

const BODY_COMPOSITION_DISPLAY = {
  id: "body-composition",
  name: "Body Composition",
  unit: "",
} as const;

interface BodyMeasurementsProps {
  patientId: string;
  onViewHistory: () => void;
}

// Individual metric calls - only for overview
const bodyMeasurementsQueries = [
  {
    queryKey: "height",
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.Height,
  },
  {
    queryKey: "weight",
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.Weight,
  },
  {
    queryKey: "bodyFatPercentage",
    metricCode:
      MetricValuesAggregationRequestDtoMetricCodeEnum.BodyFatPercentage,
  },
  {
    queryKey: "bmi",
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.BodyMassIndex,
  },
  {
    queryKey: "leanBodyMass",
    metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.LeanBodyMass,
  },
];

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return "No Data";

  // Handle numeric values
  if (typeof value === "number") {
    return value.toString();
  }

  return "No Data";
};

const getFallbackInfo = (): MetricData["fallbackInfo"] => ({
  message: "No recent data available",
  suggestion: "Consider manual measurement",
  lastRecorded: "Not available",
});

const BodyMeasurements: React.FC<BodyMeasurementsProps> = ({
  patientId,
  onViewHistory,
}) => {
  const queryResults = useQueries({
    queries: bodyMeasurementsQueries.map((query) => ({
      queryKey: ["bodyMeasurements", patientId, query.queryKey],
      queryFn: () => {
        if (!patientId) return null;

        const request: MetricValuesAggregationRequestDto = {
          metricCode: query.metricCode,
          healthProviderCode:
            MetricValuesAggregationRequestDtoHealthProviderCodeEnum.AppleHealthkit,
          interval: MetricValuesAggregationRequestDtoIntervalEnum.Daily,
          aggregateTypes: [
            MetricValuesAggregationRequestDtoAggregateTypesEnum.LatestRecord,
          ],
          orderBy: "DESC",
        };

        return activityApi.getMetricValuesAggregatedByPatientId({
          patientId,
          metricValuesAggregationRequestDto: request,
        });
      },
      enabled: !!patientId,
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    })),
  });

  const metricDataMap: Record<
    string,
    MetricValueAggregatedResponseDto | undefined
  > = useMemo(() => {
    const map: Record<string, MetricValueAggregatedResponseDto | undefined> =
      {};
    bodyMeasurementsQueries.forEach((q, idx) => {
      const res = queryResults[idx];
      map[q.metricCode] = res?.data?.data?.[0];
    });
    return map;
  }, [queryResults]);

  const displayData: MetricData[] = useMemo(() => {
    // Height card
    const heightValue =
      metricDataMap[MetricValuesAggregationRequestDtoMetricCodeEnum.Height];
    const heightCard: MetricData = heightValue
      ? {
          id: HEIGHT_DISPLAY.id,
          name: HEIGHT_DISPLAY.name,
          value: formatValue(
            heightValue.valueJsonData?.latest ??
              heightValue.valueIntData?.latest ??
              heightValue.valueFloatData?.latest,
          ),
          unit: HEIGHT_DISPLAY.unit,
          lastChecked: new Date(heightValue.startDate).toLocaleString("vi-VN"),
          hasData: true,
        }
      : {
          id: HEIGHT_DISPLAY.id,
          name: HEIGHT_DISPLAY.name,
          value: "No Data",
          unit: HEIGHT_DISPLAY.unit,
          lastChecked: "Never",
          hasData: false,
          fallbackInfo: getFallbackInfo(),
        };

    // Body Composition combines 4 metrics
    const compositionCodes = [
      MetricValuesAggregationRequestDtoMetricCodeEnum.Weight,
      MetricValuesAggregationRequestDtoMetricCodeEnum.BodyFatPercentage,
      MetricValuesAggregationRequestDtoMetricCodeEnum.BodyMassIndex,
      MetricValuesAggregationRequestDtoMetricCodeEnum.LeanBodyMass,
    ];

    const availableCompositions = compositionCodes
      .map((code) => metricDataMap[code])
      .filter(Boolean) as MetricValueAggregatedResponseDto[];

    let bodyCompositionCard: MetricData;
    if (availableCompositions.length > 0) {
      const latestDate = availableCompositions
        .map((d) => new Date(d.startDate).getTime())
        .reduce((a, b) => Math.max(a, b), 0);

      bodyCompositionCard = {
        id: BODY_COMPOSITION_DISPLAY.id,
        name: BODY_COMPOSITION_DISPLAY.name,
        value: "All Records",
        unit: BODY_COMPOSITION_DISPLAY.unit,
        lastChecked: new Date(latestDate).toLocaleString("vi-VN"),
        hasData: true,
      };
    } else {
      bodyCompositionCard = {
        id: BODY_COMPOSITION_DISPLAY.id,
        name: BODY_COMPOSITION_DISPLAY.name,
        value: "No Data",
        unit: BODY_COMPOSITION_DISPLAY.unit,
        lastChecked: "Never",
        hasData: false,
        fallbackInfo: getFallbackInfo(),
      };
    }

    return [heightCard, bodyCompositionCard];
  }, [metricDataMap]);

  const isLoading = useMemo(
    () => queryResults.some((q) => q.isLoading),
    [queryResults],
  );

  if (isLoading) {
    return (
      <SectionWrapper title="Body Measurements" onViewAll={onViewHistory}>
        <Typography>Loading body measurements data...</Typography>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      title="Body Measurements"
      onViewAll={onViewHistory}
      spacing="compact"
    >
      {displayData.map((metric) => (
        <MetricCard key={metric.id} metric={metric} />
      ))}
    </SectionWrapper>
  );
};

export default BodyMeasurements;
